/**
 * Wire + view types for the payments-runtime price book (`payment_fees`).
 *
 * Every money field is a decimal STRING at 8 dp (`"8.00000000"`) and `percentage` a decimal string at 4 dp —
 * exactly as `numeric(20,8)` / `numeric(7,4)` serialize. `tiers` is the single exception: its members are
 * JSON numbers, because the backend DTO declares them `@IsNumber()`. Never widen a money string to `number`.
 */

import type { AppliedTax } from "../utils/taxMath";
import type { ProviderRailRoute } from "./paymentRoutes.types";

/** Mirrors coloca-nest `PaymentFeeKind`. */
export const PaymentFeeKind = {
  /** What the PSP charges Coloca — a cost. Scoped by provider, never by organization. */
  PROVIDER_COST: "PROVIDER_COST",
  /** What Coloca charges the client organization — revenue. Scoped by organization, never by provider. */
  COLOCA_COMMISSION: "COLOCA_COMMISSION",
} as const;
export type PaymentFeeKind = (typeof PaymentFeeKind)[keyof typeof PaymentFeeKind];

/** Mirrors coloca-nest `PaymentDirection`. */
export const PaymentDirection = {
  PAYIN: "PAYIN",
  PAYOUT: "PAYOUT",
} as const;
export type PaymentDirection = (typeof PaymentDirection)[keyof typeof PaymentDirection];

/**
 * Mirrors coloca-nest `EconomicModel`. Lives on the corridor's provider route, NOT on the fee row: it decides
 * whether the provider cost is billed on to the client (`PASS_THROUGH`) or paid out of Coloca's commission
 * (`ABSORBED`, where the margin can go negative).
 */
export const EconomicModel = {
  PASS_THROUGH: "PASS_THROUGH",
  ABSORBED: "ABSORBED",
} as const;
export type EconomicModel = (typeof EconomicModel)[keyof typeof EconomicModel];

/** Mirrors coloca-nest `Rail`. Render labels through `formatRail` — rail names are never translated. */
export const PaymentFeeRail = {
  SPEI: "SPEI",
  PSE: "PSE",
  TRANSFIYA: "TRANSFIYA",
  BREB: "BREB",
  NEQUI: "NEQUI",
  BANCOLOMBIA_BUTTON: "BANCOLOMBIA_BUTTON",
  PIX: "PIX",
  CBU_CVU: "CBU_CVU",
  USDT: "USDT",
} as const;
export type PaymentFeeRail = (typeof PaymentFeeRail)[keyof typeof PaymentFeeRail];

/**
 * Range of the amount of ONE operation (never accumulated volume). JSON numbers on the wire (see the file
 * header). A match REPLACES the base fixed+percentage and applies to the whole amount.
 */
export interface PaymentFeeTier {
  threshold: number;
  fixed: number;
  percentage: number;
}

/** Row of `GET /internal/payments/fees` (coloca-nest `PaymentFeeEntity`). */
export interface PaymentFee {
  id: string;
  fee_kind: PaymentFeeKind;
  /** NULL = any provider. Only ever set on `PROVIDER_COST` rows. */
  provider_code: string | null;
  /** NULL = any organization. Only ever set on `COLOCA_COMMISSION` rows. */
  organization_id: string | null;
  rail: PaymentFeeRail | null;
  /** ISO alpha-3. */
  country: string | null;
  currency: string | null;
  direction: PaymentDirection | null;
  fixed: string;
  percentage: string;
  min_fee: string;
  max_fee: string | null;
  tiers: PaymentFeeTier[] | null;
  valid_from: string;
  valid_to: string | null;
  is_active: boolean;
  version: number;
  created_by_user_id: string | null;
  change_reason: string | null;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
}

/** Body of `POST /internal/payments/fees` (`CreatePaymentFeeDto`). Only `fee_kind` is required. */
export interface CreatePaymentFeePayload {
  fee_kind: PaymentFeeKind;
  provider_code?: string;
  organization_id?: string;
  rail?: PaymentFeeRail;
  country?: string;
  currency?: string;
  direction?: PaymentDirection;
  fixed?: string;
  percentage?: string;
  min_fee?: string;
  max_fee?: string;
  tiers?: PaymentFeeTier[];
  valid_from?: string;
  valid_to?: string;
  change_reason?: string;
}

/** Query of `GET /internal/payments/fees`. */
export interface ListPaymentFeesParams {
  fee_kind?: PaymentFeeKind;
  provider_code?: string;
  organization_id?: string;
  rail?: PaymentFeeRail;
  country?: string;
  currency?: string;
  direction?: PaymentDirection;
  is_active?: boolean;
  skip?: number;
  limit?: number;
}

/**
 * The corridor catalog row now lives with the rest of the provider-routes contract, next to its create/query
 * payloads and its derived cell types. Re-exported here because the price book reads it as a catalog (a fee's
 * `PROVIDER_COST` scope and every corridor's `economic_model` come from it) — a type-only re-export, so
 * neither module gains a runtime dependency on the other.
 */
export type { ProviderRailRoute } from "./paymentRoutes.types";

/* ---------------------------------- view types ---------------------------------- */

/**
 * Lifecycle state derived client-side, because the backend never closes the previous version's `valid_to`:
 * two rows of the same scope can both be `is_active` and the resolver just prefers the later `valid_from`.
 * `SUPERSEDED` is precisely "still live in the database, but the resolver will never pick it".
 */
export type FeeRowStatus = "ACTIVE" | "SCHEDULED" | "SUPERSEDED" | "EXPIRED" | "DISABLED";

/** The six nullable scope dimensions the resolver scores. `null` means "applies to any". */
export interface FeeScope {
  provider_code: string | null;
  organization_id: string | null;
  rail: PaymentFeeRail | null;
  country: string | null;
  currency: string | null;
  direction: PaymentDirection | null;
}

export interface FeeVersionRow {
  fee: PaymentFee;
  status: FeeRowStatus;
}

/** All rows sharing one exact scope tuple, newest `valid_from` first. */
export interface FeeRuleGroup {
  /** Stable key built from `fee_kind` + the six scope columns. */
  key: string;
  scope: FeeScope;
  feeKind: PaymentFeeKind;
  /** Count of non-null scope columns (0–6) — the resolver's primary ranking. */
  specificity: number;
  rows: FeeVersionRow[];
  /** The row the resolver would pick today; falls back to the next scheduled/most recent one for display. */
  winner: FeeVersionRow;
}

/** Result of applying one fee row to an amount. All amounts are decimal strings. */
export interface FeeComputation {
  total: string;
  /** 1-based index of the matched tier in threshold-ascending order; `null` when no tier applied. */
  tierIndex: number | null;
  clampedToMin: boolean;
  clampedToMax: boolean;
  min: string;
  max: string | null;
}

/** Which rule produced the resolved commission — the same figure can come from either. */
export type FeeCommissionSource = "GLOBAL" | "ORGANIZATION";

/** An organization whose own rule is what it actually pays on a corridor. */
export interface CorridorOrgOverride {
  organizationId: string;
  fee: PaymentFee;
}

/** One corridor's coverage + economics at its currency's example amount, resolved for the current scope. */
export interface CorridorCoverage {
  route: ProviderRailRoute;
  /** The operation amount every figure of this row is computed on, in the corridor's currency. */
  exampleAmount: string;
  commission: PaymentFee | null;
  cost: PaymentFee | null;
  commissionAmount: string | null;
  costAmount: string | null;
  commissionComputation: FeeComputation | null;
  costComputation: FeeComputation | null;
  /** `null` when the corridor has no resolvable commission at all. */
  commissionSource: FeeCommissionSource | null;
  /** The rule that would apply without an organization override — what an override is measured against. */
  globalCommission: PaymentFee | null;
  globalCommissionAmount: string | null;
  /** Organization scope only: an own rule that matches here but loses to a more specific general rule. */
  ownRuleShadowed: PaymentFee | null;
  /** General scope only: organizations paying their own price on this corridor. */
  orgOverrides: CorridorOrgOverride[];
  /** A rule that starts later will change this corridor's price in the current scope. */
  hasScheduledChange: boolean;
  /** Taxes on this corridor at the example amount, whoever bears them. */
  taxes: AppliedTax[];
  /** Model-aware total charged to the client, its taxes included. */
  clientPays: string | null;
  /**
   * `commission` under pass-through, `commission − cost` under absorbed, less the taxes Coloca absorbs. Can
   * be negative.
   */
  margin: string | null;
  status: "PRICED" | "PARTIAL" | "UNPRICED";
}

export type FeeTabId = "corridors" | "history" | "commissions" | "costs";

/** The corridors tab's KPI strip doubles as its filter. */
export type CorridorFilter = "all" | "unpriced" | "special" | "negative" | "scheduled";

export interface FeeFiltersState {
  /** `"MEX|SPEI|MXN"` — one pick that maps to the three separate query params. */
  corridor: string;
  direction: PaymentDirection | "";
  organizationId: string;
  providerCode: string;
  /** Client-side: the API cannot filter on derived status. */
  showSuperseded: boolean;
  showScheduled: boolean;
}
